<script>
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import VideoPlayer from "@/components/CustomComponent/shared/VidioPlayer";
import { useAuthStore } from "@/stores/auth-store";
import { Modal } from "bootstrap";
import { useCourseDataStore } from "@/stores/course/course-data-store";
import { useSharedStore } from "@/stores/shared-store";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import { useExamDataStore } from "@/stores/exam/exam-data-store";
import Swal from "sweetalert2";
import { useEnrollmentStore } from "@/stores/access/enrollment-store.js";
import PdfViewer from "@/components/CustomComponent/shared/PdfViewer.vue";

export default {
  props: {
    courseID: {
      type: [String, Number],
    },
    enrollmentID: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  components: {
    VideoPlayer,
    BaseLoading,
    PdfViewer,
  },

  data() {
    return {
      currentIndex: 0,
      selectedOption: null,
      videoEnded: false,
      currentEnrollment: null,
      activeQuiz: null,
      quizQueue: [],
    };
  },
  computed: {
    sharedStore() {
      return useSharedStore();
    },

    courseDataStore() {
      return useCourseDataStore();
    },
    authStore() {
      return useAuthStore();
    },
    courseFetchStore() {
      return useCourseFetchStore();
    },
    examDataStore() {
      return useExamDataStore();
    },
    enrollmentStore() {
      return useEnrollmentStore();
    },
    isStudent() {
      return this.authStore.role?.toLowerCase() === "student";
    },
    isPrincipal() {
      return this.authStore.role?.toLowerCase() === "principal";
    },
    isInstructor() {
      const role = this.authStore.role?.toLowerCase();
      return role === "teacher";
    },
    isPrincipalOrInstructor() {
      return this.isPrincipal || this.isInstructor;
    },

    currentContent() {
      return (
        this.courseFetchStore.basicCourseDetail?.content?.[this.currentIndex] ||
        null
      );
    },
    vidioURL() {
      if (!this.currentContent?.filePath) return null;
      return this.authStore.Url + this.currentContent.filePath;
    },
    fileUrl() {
      if (!this.authStore.Url || !this.currentContent?.filePath) return null;
      return this.formatFileUrl(
        this.authStore.Url,
        this.currentContent.filePath
      );
    },
    pdfViewerUrl() {
      if (!this.fileUrl) return null;
      if (this.isStudent) {
        return `${this.fileUrl}#toolbar=0&navpanes=0`;
      }
      return this.fileUrl;
    },
    /** True jika fileUrl bisa di-fetch publik oleh Office Online (bukan localhost). */
    canEmbedPptViewer() {
      if (!this.fileUrl) return false;
      try {
        const url = new URL(this.fileUrl);
        return (
          url.protocol === "https:" &&
          url.hostname !== "localhost" &&
          url.hostname !== "127.0.0.1"
        );
      } catch {
        return false;
      }
    },
    pptViewerUrl() {
      if (!this.canEmbedPptViewer) return null;
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        this.fileUrl
      )}`;
    },
    currentContentQuizzes() {
      const quizzes = this.courseFetchStore.basicCourseDetail?.quizzes;
      if (!quizzes?.length || !this.currentContent?.contentID) return [];
      return quizzes
        .filter((q) => q.content === this.currentContent.contentID)
        .slice()
        .sort((a, b) => Number(a.popup_time) - Number(b.popup_time));
    },
    /** Quiz yang sedang ditampilkan di modal (dari antrian) */
    currentQuiz() {
      return this.activeQuiz;
    },
    /** Timestamp unik yang masih perlu di-trigger untuk video ini */
    quizTimes() {
      if (this.isCompleted) return []; // Review mode: student tidak perlu ikut quiz
      return [
        ...new Set(
          this.currentContentQuizzes
            .filter((q) => this.shouldTriggerQuiz(q))
            .map((q) => Number(q.popup_time))
            .filter((t) => !Number.isNaN(t) && t >= 0)
        ),
      ].sort((a, b) => a - b);
    },
    currentQuizIndex() {
      if (!this.currentQuiz) return null;
      const idx = this.currentContentQuizzes.findIndex(
        (q) => q.quiz_id === this.currentQuiz.quiz_id
      );
      return idx >= 0 ? idx : null;
    },
    currentQuizNumber() {
      if (this.currentQuizIndex === null) return null;
      return this.currentQuizIndex + 1;
    },
    currentQuizTotal() {
      return this.currentContentQuizzes.length;
    },
    isRemedial() {
      return this.enrollmentStore.enrollmentDetail?.status === "Remedial";
    },
    isCompleted() {
      const detailStatus = this.enrollmentStore.enrollmentDetail?.status;
      if (detailStatus === "Completed") return true;
      const studentEnrollment = this.enrollmentStore.enrollmentStudent?.find(
        (e) => String(e.courseID?.course_id || e.courseID) === String(this.courseID)
      );
      return studentEnrollment?.status === "Completed";
    },
    isReviewMode() {
      return this.isPrincipalOrInstructor || this.isCompleted;
    },
    isNextDisabled() {
      const isLastContent =
        this.currentIndex ===
        this.courseFetchStore.basicCourseDetail.content?.length - 1;

      // Principal, Instructor, remedial, dan student yang sedang review (completed) bebas akses
      if (this.isReviewMode || this.isRemedial) {
        return isLastContent;
      }

      // Kalau bukan video, langsung bisa next kecuali kalau sudah terakhir
      if (this.currentContent?.type !== "Video") {
        return isLastContent;
      }

      // Kalau video, cek apakah sudah selesai
      return !this.videoEnded || isLastContent;
    },
  },
  watch: {
    currentIndex() {
      this.resetQuizSession();
      this.videoEnded = false;
    },
  },
  async mounted() {
    const role = this.authStore.role?.toLowerCase();

    await this.hydrateCourseDetail();

    if (role === "student") {
      // khusus student
      if (this.authStore.currentUser?.id) {
        this.courseFetchStore.fetchStudentQuizSubmission(
          this.authStore.currentUser.id,
          this.courseID
        );
      }
      if (this.enrollmentID && this.enrollmentID !== "undefined") {
        this.enrollmentStore.fetchEnrollmentDetail(this.enrollmentID);
      }
    }

    const modalEl = document.getElementById("simpleModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", this.onQuizModalHidden);
    }
  },
  beforeUnmount() {
    const modalEl = document.getElementById("simpleModal");
    if (modalEl) {
      modalEl.removeEventListener("hidden.bs.modal", this.onQuizModalHidden);
    }
  },
  methods: {
    seedCourseFromEnrollment() {
      if (!this.courseID) return;
      const enrolled = this.enrollmentStore.enrollmentStudent?.find(
        (e) =>
          String(e.courseID?.course_id || e.courseID) === String(this.courseID)
      );
      if (enrolled?.courseID && typeof enrolled.courseID === "object") {
        this.courseFetchStore.setBasicCourseDetail(enrolled.courseID);
      }
    },
    async hydrateCourseDetail() {
      // Pakai data yang sudah di-seed dari resume, lalu refresh dari API
      const cachedId = this.courseFetchStore.basicCourseDetail?.course_id;
      if (String(cachedId) !== String(this.courseID)) {
        this.seedCourseFromEnrollment();
      }
      await this.courseFetchStore.fetchCourseDetailBasic(this.courseID);
      if (!this.courseFetchStore.basicCourseDetail?.course_id) {
        this.seedCourseFromEnrollment();
      }
    },
    shouldTriggerQuiz(quiz) {
      if (this.isPrincipalOrInstructor) return true;
      if (this.isCompleted) return false; // Review mode: student tidak perlu ikut quiz
      return !this.isQuizSubmitted(quiz.quiz_id);
    },
    resetQuizSession() {
      this.activeQuiz = null;
      this.quizQueue = [];
      this.selectedOption = null;
    },
    getContentTitle(contentId) {
      const content = this.courseFetchStore.basicCourseDetail.content.find(
        c => c.contentID === contentId
      )
      return content ? content.title : '-'
    },
    formatVidioTime(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    isQuizSubmitted(quizId) {
      return this.courseFetchStore.studentQuisSubmition?.some(
        (submission) => submission.quiz === quizId
      );
    },
    onVideoEnded() {
      this.videoEnded = true;
    },
    formatFileUrl(baseUrl, filePath) {
      if (typeof baseUrl !== "string" || typeof filePath !== "string") {
        return "";
      }
      if (baseUrl.endsWith("/")) {
        baseUrl = baseUrl.slice(0, -1);
      }
      if (!filePath.startsWith("/")) {
        filePath = "/" + filePath;
      }
      return baseUrl + filePath;
    },
    canGoToContent(index) {
      if (index === this.currentIndex) return true;
      if (this.isReviewMode || this.isRemedial) return true;
      // Student: boleh loncat ke materi yang sudah / sedang dibuka
      return index <= this.currentIndex;
    },
    goToContent(index) {
      const contents = this.courseFetchStore.basicCourseDetail?.content;
      if (!contents?.length || index < 0 || index >= contents.length) return;
      if (index === this.currentIndex) return;
      if (!this.canGoToContent(index)) return;
      this.currentIndex = index;
    },
    prevContent() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    nextContent() {
      if (
        this.courseFetchStore.basicCourseDetail?.content &&
        this.currentIndex <
        this.courseFetchStore.basicCourseDetail.content.length - 1
      ) {
        this.currentIndex++;
      }
    },
    handleQuizTrigger(popupTime) {
      const time = Number(popupTime);
      const quizzesAtTime = this.currentContentQuizzes.filter(
        (q) =>
          Number(q.popup_time) === time && this.shouldTriggerQuiz(q)
      );

      if (!quizzesAtTime.length) return;

      // Antrikan semua quiz di detik yang sama (urut sesuai list)
      this.quizQueue = [...quizzesAtTime];
      this.showNextQuizInQueue();
    },
    showNextQuizInQueue() {
      if (!this.quizQueue.length) {
        this.activeQuiz = null;
        this.selectedOption = null;
        return;
      }

      this.activeQuiz = this.quizQueue.shift();
      this.selectedOption = null;

      const modalEl = document.getElementById("simpleModal");
      if (modalEl) {
        const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
        modal.show();
      }
    },
    onQuizModalHidden() {
      // Setelah modal ditutup, lanjut antrian quiz berikutnya (jika ada)
      if (this.quizQueue.length > 0) {
        setTimeout(() => this.showNextQuizInQueue(), 250);
      } else {
        this.activeQuiz = null;
        this.selectedOption = null;
      }
    },
    closeQuizModal() {
      const modalEl = document.getElementById("simpleModal");
      if (modalEl) {
        const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
        modal.hide();
      }
    },
    handelAllowQuizTrigger() {
      if (this.isCompleted) return false;
      return this.quizTimes.length > 0;
    },
    async handleSubmitQuiz() {
      if (this.isPrincipalOrInstructor) {
        this.sharedStore.showError("Principal/Instructor tidak bisa submit quiz");
        return;
      }

      if (!this.selectedOption) {
        this.sharedStore.showError("Silakan pilih jawaban terlebih dahulu!");
        return;
      }
      if (!this.currentQuiz?.quiz_id) {
        this.sharedStore.showError("Quiz tidak valid.");
        return;
      }

      this.courseDataStore.quizAnswerDraft = {
        student: this.authStore.currentUser.id,
        quiz: this.currentQuiz.quiz_id,
        selected_answer: this.selectedOption,
        is_correct: this.selectedOption === this.currentQuiz.correct_answer,
      };
      await this.courseDataStore.submitQuisAnswer();
      this.closeQuizModal();
    },
    async goToExam() {
      if (this.isCompleted) {
        Swal.fire({
          title: "Course Selesai",
          text: "Kamu sudah menyelesaikan course ini dan telah lulus ujian.",
          icon: "info",
        });
        return;
      }

      let slug;
      const examID = this.courseFetchStore.basicCourseDetail?.exam?.examID;
      const studentID = this.authStore.currentUser.id;
      const examType = this.courseFetchStore.basicCourseDetail?.exam.examType;

      // === Handle Practical Exam ===
      if (examType === "Practical") {
        if (this.isPrincipalOrInstructor) {
          // Principal & Instructor langsung kembali ke halaman CourseDetail
          this.$router.push({
            name: "CourseDetail",
            params: {
              course_id: this.courseID,
            },
          });
          return;
        }
        // Selain principal → jalankan action createPractical
        try {
          await this.examDataStore.createSubmission(examID, studentID);
          Swal.fire({
            title: "Upload Video Practical",
            text: "Jangan lupa untuk mengunggah video practical sesuai instruksi yang diberikan.",
            icon: "warning",
            confirmButtonText: "Mengerti",
          }).then(() => {
            this.$router.push("/students/student-exam");
          });
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: "Error",
            text: "Gagal membuat practical.",
            icon: "error",
          });
        }
        return;
      }
      // === Handle Written & Both ===
      if (this.isPrincipalOrInstructor) {
        slug = btoa(`${this.courseID}-${examID}`);
        this.$router.push({
          name: "CourseExam",
          params: {
            slug,
          },
        });
        return;
      }

      Swal.fire({
        title: "Mulai Ujian?",
        text: "Pastikan kamu sudah siap sebelum memulai. Waktu akan langsung berjalan setelah masuk ujian.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, mulai sekarang",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.handleExamStart(examID, studentID, this.courseId);
        }
      });
    },

    async handleExamStart(examID, studentID, courseID) {
      try {
        const lastSubmission = await this.examDataStore.getLastSubmission(
          examID,
          studentID
        );
        console.log(lastSubmission);

        // 🔎 Cek apakah sudah attempt 3 kali
        if (lastSubmission && lastSubmission.attempt >= 3) {
          await this.handleRemedialFlow(examID, courseID, studentID);
        } else {
          await this.handleNormalFlow(lastSubmission, examID, studentID);
        }
      } catch (error) {
        console.error("Gagal membuat submission:", error);
        Swal.fire("Error", "Gagal membuat submission. Coba lagi.", "error");
      }
    },

    async handleRemedialFlow(examID, CourseID, studentID) {
      const remedial = await this.accessClassRequestStore.getLatestRemedial(
        CourseID,
        studentID
      );

      if (remedial && remedial.status === "Approved" && !remedial.used) {
        // ✅ sudah dapat ijin remedial → buat submission baru
        const submission = await this.examDataStore.createSubmission(
          examID,
          studentID
        );
        this.startExam(submission, examID);
      } else {
        // ❌ belum ada remedial → minta request
        Swal.fire({
          title: "Batas Attempt Tercapai",
          text: "Kamu sudah 3x attempt. Ajukan request remedial ke Principal?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ya, Ajukan",
          cancelButtonText: "Batal",
        }).then(async (res) => {
          if (res.isConfirmed) {
            await this.accessClassRequestStore.requestRemedial(
              CourseID,
              studentID
            );
          }
        });
      }
    },

    async handleNormalFlow(lastSubmission, examID, studentID) {
      let submission;
      if (lastSubmission && !this.isSubmissionExpired(lastSubmission)) {
        submission = lastSubmission;
      } else {
        submission = await this.examDataStore.createSubmission(
          examID,
          studentID
        );
      }

      this.startExam(submission, examID);
    },

    startExam(submission, examID) {
      const slug = btoa(`${this.courseID}-${examID}`);
      localStorage.setItem(
        "exam_session",
        JSON.stringify({
          examID: examID,
          submissionID: submission.submissionID,
          answers: [],
        })
      );
      console.log(examID)
      this.$router.push({
        name: "CourseExam",
        params: { slug },
      });
    },
    isSubmissionExpired(submission) {
      if (!submission?.endTime) {
        return true; // anggap expired kalau tidak ada endTime
      }

      const now = new Date();
      const endTime = new Date(submission.endTime);

      return now > endTime;
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Course watch</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Course watch
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <!-- dialog quiz -->
  <!-- Button trigger modal -->
  <!-- dialog quiz -->

  <!-- Course watch -->
  <div class="content pt-0">
    <div class="container-fluid">
      <div class="course-watch-section">
        <div class="row">
          <div class="col-lg-4 border-end">
            <div class="progress-overview-section">
              <div class="mb-4">
                <a href="javascript:void(0);" class="back-to-course" @click.prevent="
                  $router.push({
                    name: 'CourseDetail',
                    params: {
                      course_id: courseID,
                    },
                  })
                  "><i class="isax isax-arrow-left me-1"></i>Back to Course</a>
              </div>
              <h3>{{ courseFetchStore.basicCourseDetail?.title }}</h3>

              <div class="accordions-items-seperate" id="accordionSpacingExample">
                <div class="accordion-item">
                  <div class="accordion-header" id="headingSpacingOne3">
                    <div class="accordion-button collapsed" role="button" data-bs-toggle="collapse"
                      data-bs-target="#SpacingOne" aria-expanded="false" aria-controls="SpacingOne">
                      <div>
                        <h6 class="mb-0">Content</h6>
                      </div>
                    </div>
                  </div>
                  <div id="SpacingOne" class="accordion-collapse collapse show" aria-labelledby="headingSpacingOne3"
                    data-bs-parent="#accordionSpacingExample">
                    <div class="accordion-body">
                      <div v-if="!courseFetchStore.basicCourseDetail?.content?.length"
                        class="text-center text-muted fs-13 py-2">
                        Belum ada konten materi.
                      </div>
                      <template v-else>
                        <div class="d-flex align-items-center justify-content-between my-1 content-nav-item" :class="{
                          'content-nav-item--active': index === currentIndex,
                          'content-nav-item--disabled': !canGoToContent(index),
                        }" role="button" tabindex="0" v-for="(item, index) in courseFetchStore
                            .basicCourseDetail.content" :key="index" @click="goToContent(index)"
                          @keydown.enter.prevent="goToContent(index)">
                          <div class="d-flex align-items-center min-w-0">
                            <span class="d-flex"><i class="isax isax-play-circle5 text-success fs-24 me-1"></i></span>
                            <p class="accordian-content mb-0 text-truncate">{{ item.title }}</p>
                          </div>
                          <span class="flex-shrink-0 ms-2"> {{ item.type }} </span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <div class="accordion-item">
                  <div class="accordion-header" id="headingSpacingTwo">
                    <div class="accordion-button collapsed" role="button" data-bs-toggle="collapse"
                      data-bs-target="#SpacingTwo" aria-expanded="false" aria-controls="SpacingTwo">
                      <div>
                        <!-- <span class="d-block mb-1">Section 2</span> -->
                        <h6 class="mb-0">Quiz</h6>
                      </div>
                    </div>
                  </div>
                  <div id="SpacingTwo" class="accordion-collapse collapse" aria-labelledby="headingSpacingTwo"
                    data-bs-parent="#accordionSpacingExample">
                    <div class="accordion-body">
                      <div v-if="!courseFetchStore.basicCourseDetail?.quizzes?.length"
                        class="text-center text-muted fs-13 py-2">
                        Belum ada quiz untuk course ini.
                      </div>
                      <template v-else>
                        <div class="d-flex align-items-center justify-content-between my-1" v-for="(item, index) in courseFetchStore
                          .basicCourseDetail.quizzes" :key="index">
                          <div class="d-flex align-items-center">
                            <span class="d-flex"><i class="isax isax-play-circle5 text-success fs-24 me-1"></i></span>
                            <p class="accordian-content mb-0">
                              Quiz #{{ index + 1 }}
                            </p>
                          </div>
                          <span class="me-2"> {{ formatVidioTime(item.popup_time) }} </span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <div class="accordion-header" id="headingSpacingThree">
                    <div class="accordion-button collapsed" role="button" data-bs-toggle="collapse"
                      data-bs-target="#SpacingThree" aria-expanded="false" aria-controls="SpacingThree">
                      <div>
                        <h6 class="mb-0">Exam</h6>
                      </div>
                    </div>
                  </div>
                  <div id="SpacingThree" class="accordion-collapse collapse" aria-labelledby="headingSpacingThree"
                    data-bs-parent="#accordionSpacingExample">
                    <div class="accordion-body">
                      <div v-if="!courseFetchStore.basicCourseDetail?.exam" class="text-center text-muted fs-13 py-2">
                        Tidak ada ujian akhir (exam) yang dikonfigurasi.
                      </div>
                      <div v-else class="d-flex align-items-center justify-content-between my-1">
                        <div class="d-flex align-items-center">
                          <span class="d-flex"><i class="isax isax-play-circle5 text-success fs-24 me-1"></i></span>
                          <p class="accordian-content mb-0">Exam</p>
                        </div>
                        <span>{{
                          courseFetchStore.basicCourseDetail.exam
                            ?.time_limit_minutes + " minutes"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-8">
            <div class="course-watch-content">
              <!-- Vidio -->
              <div class="position-relative video-btn" v-if="currentContent?.type === 'Video'">
                <VideoPlayer :key="currentContent?.contentID || currentIndex" :src="vidioURL" :quizTimes="quizTimes"
                  :allowQuizTrigger="handelAllowQuizTrigger()" @quiz-trigger="handleQuizTrigger"
                  @video-ended="onVideoEnded" :isPrincipal="isReviewMode" />
              </div>
              <!-- PDF -->
              <div class="position-relative video-btn" v-if="currentContent?.type === 'PDF' && fileUrl">
                <PdfViewer :key="fileUrl" :src="fileUrl" :isStudent="isStudent" />
              </div>
              <!-- Slide -->
              <div class="position-relative video-btn" v-if="currentContent?.type === 'File PPT' && fileUrl">
                <div v-if="pptViewerUrl" class="position-relative w-100" style="height: 70vh; min-height: 590px;">
                  <iframe :key="pptViewerUrl" :src="pptViewerUrl" width="100%" height="100%"
                    style="border: none; height: 100%; min-height: 590px" frameborder="0" allowfullscreen></iframe>
                  <!-- Overlay penutup menu kanan bawah (Download/Print) khusus student -->
                  <div v-if="isStudent" class="position-absolute"
                    style="bottom: 0; right: 0; width: 180px; height: 42px; background: #262626; z-index: 10; cursor: default;"
                    @click.stop.prevent @contextmenu.stop.prevent title="Menu download dinonaktifkan"></div>
                </div>
                <div v-else
                  class="d-flex flex-column align-items-center justify-content-center text-center border rounded p-4"
                  style="height: 70vh; min-height: 590px; background: #f8f9fa">
                  <i class="isax isax-document-text fs-40 text-muted mb-3"></i>
                  <p class="mb-1 fw-medium">Preview PPT tidak tersedia.</p>
                  <p class="text-muted fs-13 mb-3">
                    Office Online hanya bisa memuat file publik (HTTPS). Silakan download untuk membuka file.
                  </p>
                  <a v-if="isPrincipalOrInstructor" class="btn btn-danger" :href="fileUrl"
                    :download="currentContent?.title || 'document.pptx'" target="_blank" rel="noopener noreferrer">
                    Download PPT
                  </a>
                </div>
              </div>
              <!-- <div class="position-relative video-btn">
                <a
                  href="https://www.youtube.com/embed/1trvO6dqQUI"
                  id="openVideoBtn"
                  target="_blank"
                >
                  <img
                    class="img-fluid"
                    src="@/assets/img/course/course-27.jpg"
                    alt="img"
                  />
                  <div class="play-icon">
                    <i class="fa-solid fa-play fs-28"></i>
                  </div>
                </a>
              </div>
              <div id="videoModal">
                <div class="modal-content1">
                  <span class="close-btn" id="closeModal">&times;</span>
                  <iframe id="youtubeIframe" allowfullscreen></iframe>
                </div>
              </div> -->
              <ul class="nav-tabs mb-4 nav-justified border-0 nav-style-1 d-sm-flex d-block justify-content-end"
                role="tablist">
                <!-- <li class="nav-item">
                                <a class="btn nav-link" data-bs-toggle="tab" role="tab" href="#overview"
                                    aria-selected="false">Overview</a>
                                </li> -->
                <!-- Tombol Download PDF, muncul hanya jika konten aktif bertipe PDF/PPT dan role adalah Principal atau Instructor -->
                <li class="nav-item active"
                  v-if="isPrincipalOrInstructor && (currentContent?.type === 'PDF' || currentContent?.type === 'File PPT') && fileUrl">
                  <a class="btn nav-link" :href="fileUrl"
                    :download="currentContent?.title || (currentContent?.type === 'PDF' ? 'document.pdf' : 'document.pptx')"
                    target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                </li>
                <li class="nav-item active">
                  <button class="btn nav-link" @click="prevContent" :disabled="currentIndex === 0">
                    Prev
                  </button>
                </li>
                <li class="nav-item active">
                  <button class="btn nav-link" @click="nextContent" :disabled="isNextDisabled">
                    Next
                  </button>
                </li>
                <li class="nav-item active" v-if="
                  currentIndex ===
                  courseFetchStore.basicCourseDetail.content?.length - 1 &&
                  enrollmentStore.enrollmentDetail?.status !== 'Completed'
                ">
                  <!-- v-if="
                    currentIndex ===
                    courseFetchStore.basicCourseDetail.content?.length - 1
                  " -->
                  <button class="btn nav-link active" @click="goToExam">
                    {{
                      isPrincipalOrInstructor
                        ? courseFetchStore.basicCourseDetail?.exam?.examType ===
                          "Practical"
                          ? "Back"
                          : "Exam"
                        : courseFetchStore.basicCourseDetail?.exam?.examType ===
                          "Practical"
                          ? "Practical Exam"
                          : "Exam"
                    }}
                  </button>
                </li>
              </ul>
              <div class="tab-content">
                <div class="tab-pane active show" id="overview" role="tabpanel">
                  <div class="mb-4">
                    <h6 class="fs-18 fw-semibold mb-1">About this course</h6>
                    <p>
                      {{ courseFetchStore.basicCourseDetail.short_description }}
                    </p>
                  </div>
                  <div class="mb-4">
                    <h6 class="fs-18 fw-semibold mb-2">Description</h6>
                    <p v-html="courseFetchStore.basicCourseDetail.description"></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Course watch -->

  <div class="modal fade" id="simpleModal" tabindex="-1" aria-labelledby="simpleModalLabel" aria-hidden="true"
    data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title" id="simpleModalLabel">
            Quiz {{ currentQuizNumber }}
            <span v-if="currentQuizTotal > 1" class="text-muted fs-14 fw-normal">
              / {{ currentQuizTotal }}
            </span>
            <span v-if="currentQuiz?.popup_time != null" class="text-muted fs-14 fw-normal ms-2">
              ({{ formatVidioTime(currentQuiz.popup_time) }})
            </span>
          </h5>
          <!-- <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button> -->
        </div>

        <!-- Content -->
        <form id="quisForm" @submit.prevent="handleSubmitQuiz" v-if="!isPrincipalOrInstructor">
          <div class="modal-body">
            <div class="container mt-3">
              <!-- Question -->
              <h6 class="mb-4">
                {{ currentQuiz?.question }}
              </h6>
              <div class="list-group">
                <div class="form-check list-group-item" @click="selectedOption = 'A'">
                  <input class="form-check-input" type="radio" name="option" id="option1" value="A"
                    v-model="selectedOption" />
                  <label class="form-check-label" for="option1">
                    {{ currentQuiz?.option_a }}</label>
                </div>

                <div class="form-check list-group-item" @click="selectedOption = 'B'">
                  <input class="form-check-input" type="radio" name="option" id="option2" value="B"
                    v-model="selectedOption" />
                  <label class="form-check-label" for="option2">
                    {{ currentQuiz?.option_b }}
                  </label>
                </div>

                <div class="form-check list-group-item" @click="selectedOption = 'C'">
                  <input class="form-check-input" type="radio" name="option" id="option3" value="C"
                    v-model="selectedOption" />
                  <label class="form-check-label" for="option3">
                    {{ currentQuiz?.option_c }}
                  </label>
                </div>

                <div class="form-check list-group-item" @click="selectedOption = 'D'">
                  <input class="form-check-input" type="radio" name="option" id="option4" value="D"
                    v-model="selectedOption" />
                  <label class="form-check-label" for="option4">
                    {{ currentQuiz?.option_d }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <!-- <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button> -->

            <!-- button langsung ke close sebelum proses -->
            <button type="submit" class="btn btn-primary">Submit</button>

            <!-- tolong custom action jika ingin ada proses terlebih dahulu -->
            <!-- <button type="submit" form="myForm" class="btn btn-primary" data-bs-dismiss="modal">Submit</button> -->
          </div>
        </form>
        <div v-else><!-- Principal & Instructor: lihat jawaban benar -->
          <div class="modal-body">
            <div class="container mt-3">
              <h6 class="mb-4">{{ currentQuiz?.question }}</h6>
              <ul class="list-group">
                <li class="list-group-item" :class="{
                  'list-group-item-success':
                    currentQuiz?.correct_answer === 'A',
                }">
                  A. {{ currentQuiz?.option_a }}
                </li>
                <li class="list-group-item" :class="{
                  'list-group-item-success':
                    currentQuiz?.correct_answer === 'B',
                }">
                  B. {{ currentQuiz?.option_b }}
                </li>
                <li class="list-group-item" :class="{
                  'list-group-item-success':
                    currentQuiz?.correct_answer === 'C',
                }">
                  C. {{ currentQuiz?.option_c }}
                </li>
                <li class="list-group-item" :class="{
                  'list-group-item-success':
                    currentQuiz?.correct_answer === 'D',
                }">
                  D. {{ currentQuiz?.option_d }}
                </li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeQuizModal">
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- <footer-two></footer-two> -->
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>

<style scoped>
.content-nav-item {
  cursor: pointer;
  border-radius: 8px;
  padding: 6px 8px;
  transition: background-color 0.15s ease;
}

.content-nav-item:hover:not(.content-nav-item--disabled) {
  background-color: rgba(0, 0, 0, 0.04);
}

.content-nav-item--active {
  background-color: rgba(13, 110, 253, 0.08);
}

.content-nav-item--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
</style>
